import type { ReactNode } from "react"
import { Info } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../Tooltip/Tooltip"
import { cn } from "@/lib/utils"
import { METRIC_DEFINITIONS } from "./metricDefinitions"

export interface MetricLabelProps {
  children?: ReactNode
  label?: string
  definition?: ReactNode
  className?: string
}

export function MetricLabel({ children, label, definition, className }: MetricLabelProps) {
  // Mirror the prototype's behaviour: when no explicit `definition` is passed,
  // fall back to the shared METRIC_DEFINITIONS dictionary keyed by `label`
  // (or `children` when it is a plain string). The explicit prop still wins
  // so callers can override or supply ad-hoc definitions.
  const key = label ?? (typeof children === "string" ? children : null)
  const resolved = definition ?? (key ? METRIC_DEFINITIONS[key] : undefined)
  if (!resolved) {
    return <span className={className}>{children ?? label}</span>
  }
  return (
    <span className={cn(className, "inline-flex items-center gap-1")}>
      {children ?? label}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center cursor-help text-muted-foreground/40 hover:text-muted-foreground transition-colors">
              <Info size={10} strokeWidth={1.5} />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[260px] text-caption leading-snug">
            {resolved}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  )
}
